import { Component, OnInit } from '@angular/core';
import { Icons } from '../../../../shared/helpers/icons';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { ActivatedRoute, Router } from '@angular/router';
import { WitnessInfoComponent } from './witness-info/witness-info.component';
import { DescriptionComponent } from './description/description.component';
import { ImpressionsComponent } from './impressions/impressions.component';
import { InvolvementComponent } from './involvement/involvement.component';
import { WitnessSummaryComponent } from './witness-summary/witness-summary.component';
import { WitnessInterviewSubmissionComponent } from "./witness-interview-submission/witness-interview-submission.component";
import { UserDataService } from '../../../../shared/services/auth/userData.service';
import { WitnessInterviewService } from '../../../../shared/services/witnessInterview.service';
import { IWitnessInterview } from '../../../../shared/models/witnessInterview';
import { PopupMessageComponent } from '../../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'witness-interview',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    WitnessInfoComponent,
    InvolvementComponent,
    DescriptionComponent,
    ImpressionsComponent,
    WitnessSummaryComponent,
    WitnessInterviewSubmissionComponent,
    PopupMessageComponent,
    SpinnerComponent
],
  templateUrl: './witness-interview.component.html',
  styleUrl: './witness-interview.component.css'
})
export class WitnessInterviewComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;

  currentCaseId: string | null = null;
  currentWintessInterview: any | null = null;
  stepsConfig = [
    {
      id: 1,
      completionProperty: 'witness_interview_information_completion',
      nextStepDependency: null
    },
    {
      id: 2,
      completionProperty: 'involvement_completion',
      nextStepDependency: 'witness_interview_information_completion'
    },
    {
      id: 3,
      completionProperty: 'description_completion',
      nextStepDependency: 'involvement_completion'
    },
    {
      id: 4,
      completionProperty: 'impressions_completion',
      nextStepDependency: 'description_completion'
    },
    {
      id: 5,
      completionProperty: 'summary_completion',
      nextStepDependency: 'impressions_completion'
    }
  ];
  finalStepConfig = {
      id: 6,
      completionProperty: 'witness_interview_completion',
      nextStepDependency: 'summary_completion'
  };
  currentStep: number = 1;
  tooltips = ['Witness Information', 'Involvement', 'Description', 'Impressions', 'Summary', 'Submission'];
  messageModal: string | null = null;
  popupMessage: string | null = null;

  loading: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userDataservice: UserDataService,
    private witnessInterviewService: WitnessInterviewService,
    private toastr: ToastrService
  ) {}

  
  ngOnInit(): void {
    this.currentBuildingId = this.userDataservice.getBuildingId();
    
    this.route.paramMap.subscribe((params) => {
      this.currentCaseId = params.get('caseId');
    });
    
    this.loadWintessInterview();
  }

  loadWintessInterview(): void {
    if (this.currentCaseId) {
      this.loading = true;
      this.witnessInterviewService.getWitnessInterviewByCaseId(this.currentCaseId).subscribe({
        next: (response) => {
          if (response) {
            this.currentWintessInterview = response;
          } else {
            this.currentWintessInterview = null;
          }
          this.loading = false;
          return
        },
        error: (error) => {
          this.loading = false;
          console.error('Error:', error);
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;  
        }
      });
    }
  }

  handleUpdateWintessInterview(data: IWitnessInterview, section: number): void {
    if (this.currentCaseId && this.currentBuildingId) {
      this.loading = true;
      const fullData = data;
      fullData.case_id = this.currentCaseId!;
      fullData.section = section;
      fullData.building_id = Number(this.currentBuildingId);
      this.witnessInterviewService.updateWitnessInterviewByCaseId(fullData).subscribe({
        next: (response) => {
          this.loading = false;
          this.loadWintessInterview();
          this.toastr.success('Saved successful!', 'Success');
          this.currentStep = this.currentStep != this.finalStepConfig.id ? section + 1 : this.currentStep;
          if (section == 6 ) {
            this.router.navigate(['/assessments/']);
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error updating witness interview:', error.message);
          this.toastr.error(error.message, 'Error');
        }
      });
    }
  }

  onBack(): void {
    this.router.navigate(['/assessments/']);
  }

  // move to next section
  goToStep(stepId: number): void {
    if (this.canMoveToStep(stepId)) {
      this.currentStep = stepId;
    } else {
      console.error('You must complete the required steps before proceeding.');
    }
  }
  // check if they already completed the required step before allowing them to move to the next step
  canMoveToStep(stepId: number): boolean {
    let stepConfig;
    if (stepId === this.finalStepConfig.id) {
      stepConfig = this.finalStepConfig; // Handle the final step separately
    } else {
      stepConfig = this.stepsConfig.find(step => step.id === stepId);
    }
  
    if (!stepConfig) {
      console.error('Step configuration not found for step', stepId);
      return false;
    }
  
    if (stepConfig.nextStepDependency && this.currentWintessInterview) {
      return this.currentWintessInterview?.[stepConfig.nextStepDependency] === 1;
    }
  
    return true;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

}
